import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProfileCard.module.css';
import { setTalentVisibility } from '../../../../api';
import { applySkillOrder } from '../../../../shared/utils/skillOrder';
import { TALENT_PLACEHOLDER_SQUARE, resolveProfileImageUrl } from '../../../../shared/utils/profilePlaceholders';
const TALENT_PLACEHOLDER = TALENT_PLACEHOLDER_SQUARE;

const GENDER_KO = { MALE: '남성', FEMALE: '여성'};

function calcAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function VerifiedBadge() {
  return (
    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
        <path d="M1 4l3 3 7-7" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button onClick={onClick} className={styles.actionButton}>
      {icon}
      <span className={styles.actionButtonLabel}>{label}</span>
    </button>
  );
}

function ProfileCard({ profile }) {
  const navigate = useNavigate();
  const [isPublic, setIsPublic] = useState(profile?.visibility === 'PUBLIC');
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  useEffect(() => {
    setIsPublic(profile?.visibility === 'PUBLIC');
  }, [profile?.visibility]);

  useEffect(() => {
    if (!showCopiedToast) return undefined;
    const timer = window.setTimeout(() => setShowCopiedToast(false), 1800);
    return () => window.clearTimeout(timer);
  }, [showCopiedToast]);

  const handleToggle = async () => {
    const next = !isPublic;
    setIsPublic(next);
    try {
      await setTalentVisibility(next ? 'PUBLIC' : 'PRIVATE');
    } catch {
      setIsPublic(!next);
    }
  };

  const displayName = profile?.stageName
    ? `${profile.stageName}(${profile.name})`
    : profile?.name || '—';
  const gender = GENDER_KO[profile?.gender] ?? '';
  const age = calcAge(profile?.birthDate);
  const height = profile?.heightCm ? `${profile.heightCm}cm` : '';
  const rawSkills = profile?.skills?.map((s) => s.name || s.skillName || s) ?? [];
  const skills = applySkillOrder(profile?.id, rawSkills);

  const profileImgUrl = resolveProfileImageUrl(profile);

  const publicProfileUrl = profile?.id ? `${window.location.origin}/talents/${profile.slug || profile.id}` : window.location.href;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard?.writeText(publicProfileUrl);
      setShowCopiedToast(true);
    } catch {}
  };

  return (
    <div className={styles.card}>
      <img src={profileImgUrl || TALENT_PLACEHOLDER} alt="profile" className={styles.profileImg} loading="lazy" decoding="async" width={186} height={246} />

      <div className={styles.infoSection}>
        <div className={styles.infoContent}>
          <div className={styles.topBlock}>
            <div className={styles.nameRow}>
              <span className={styles.nameText}>{displayName}</span>
              <VerifiedBadge />
            </div>
            <div className={styles.metaRow}>
              {gender && <span className={styles.metaText}>{gender}</span>}
              {gender && age !== null && <div className={styles.divider} />}
              {age !== null && <span className={styles.metaText}>{age}세</span>}
              {(gender || age !== null) && height && <div className={styles.divider} />}
              {height && <span className={styles.metaText}>{height}</span>}
            </div>
          </div>

          <div className={styles.actionRow}>
            {/* 아이콘 내부에 남아있던 C.bk도 하드코딩된 색상으로 변경 */}
            <ActionButton label="편집" onClick={() => navigate('/talent/profile/edit')} icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            }/>
            <ActionButton label="URL 복사" onClick={handleCopyUrl} icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="13" height="13" rx="2" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            }/>
            <ActionButton label="포트폴리오 다운로드" onClick={() => navigate('/talent/profile/pdf-editor')} icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            }/>
          </div>

          {skills.length > 0 && (
            <div className={styles.skillsWrap}>
              {skills.map((s) => (
                <div key={s} className={styles.skillBadge}>
                  <span className={styles.skillText}>{s}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.visibilitySection}>
        <span className={styles.visibilityLabel}>프로필 공개</span>
        <div onClick={handleToggle} style={{ width: 57, height: 30, background: isPublic ? 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)' : '#eeeeee', borderRadius: 60, display: 'flex', alignItems: 'center', justifyContent: isPublic ? 'flex-end' : 'flex-start', padding: 4, boxSizing: 'border-box', cursor: 'pointer', transition: 'background 0.2s' }}>
          <div style={{ width: 23, height: 23, borderRadius: '50%', background: '#fff', boxShadow: '-2px 0px 8px rgba(0,0,0,0.12)' }} />
        </div>
      </div>

      {showCopiedToast && (
        <div className={styles.copyToast}>
          URL이 복사되었습니다.
        </div>
      )}
    </div>
  );
}

export default ProfileCard;
